// onboarding.utils.ts
// Utility functions for onboarding screen to reduce duplication and improve maintainability

interface BridgeApiState {
  tosStatus: string | null;
  kycStatus: string | null;
  kycLink: string | null;
  lastChecked: number | null;
  error: string | null;
}

interface BridgeApiResponse {
  tosStatus: string | null;
  kycStatus: string | null;
  kycLink: string | null;
}

/**
 * Clears onboarding-related sessionStorage items
 */
export const clearOnboardingSessionStorage = (): void => {
  sessionStorage.removeItem("onboardingTargetStep")
  sessionStorage.removeItem("onboardingNavigatedAt")
}

/**
 * Extracts common URL parameters used in onboarding
 */
export const parseOnboardingUrlParams = (search: string) => {
  const params = new URLSearchParams(search)
  return {
    step: params.get("step"),
    action: params.get("action"),
    next: params.get("next"),
  }
}

/**
 * Gets the step title for a given step number
 */
export const getStepTitle = (stepNumber: number, stepTitles: string[]): string => {
  return stepTitles[stepNumber - 1] || ""
}

/**
 * Gets the step number for a given step title
 */
export const getStepNumberByTitle = (title: string, stepTitles: string[]): number => {
  const index = stepTitles.indexOf(title)
  return index !== -1 ? index + 1 : -1
}


/**
 * Consolidated Bridge API retry logic
 */
type BridgeApiStateSetter = (
  update: Partial<BridgeApiState> | ((prev: BridgeApiState) => BridgeApiState),
) => void

export const createBridgeApiRetryHandler = (
  checkKycTosStatus: () => Promise<BridgeApiResponse>,
  setBridgeApiState: BridgeApiStateSetter,
  userBridgeId?: string,
  section?: string
) => {
  return async (retries = 3): Promise<BridgeApiResponse | null> => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      // console.log(`[OnboardingScreen] 🌉 API CALL ATTEMPT ${attempt}/${retries} for bridgeId: ${userBridgeId}`)
      try {
        const response = await checkKycTosStatus()
        // console.log(`[OnboardingScreen] 🌉 API CALL SUCCESS:`, {
        //   tosStatus: response.tosStatus,
        //   kycStatus: response.kycStatus,
        //   hasKycLink: !!response.kycLink
        // })
        setBridgeApiState({
          tosStatus: response.tosStatus,
          kycStatus: response.kycStatus,
          kycLink: response.kycLink,
          lastChecked: Date.now(),
          error: null,
        })
        return response
      } catch (error) {
        // console.error(`[OnboardingScreen] 🌉 API CALL ERROR (attempt ${attempt}/${retries}):`, error)
        const isLastAttempt = attempt === retries

        if (isLastAttempt) {
          setBridgeApiState((prev: BridgeApiState) => ({
            ...prev,
            error: error instanceof Error ? error.message : "Failed to check Bridge status",
            lastChecked: Date.now(),
          }))

          // Import Sentry dynamically to avoid circular dependencies
          import("@sentry/react").then(({ captureException }) => {
            captureException(error, {
              extra: {
                context: "Bridge status check failed after retries",
                attempts: retries,
                bridgeId: userBridgeId,
              },
              tags: { section: section || "OnboardingScreen.bridgeApiRetry" },
            })
          })
        } else {
          // Wait before retry (exponential backoff)
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt))
        }
      }
    }
    return null
  }
}